<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { AxiosError } from 'axios'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import FloatLabel from 'primevue/floatlabel'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Select from 'primevue/select'
import Slider from 'primevue/slider'
import axios, { addPrecognitionHeaders } from '@/lib/axios'
import { postSignUp } from '@js/actions/Preno/Customers/Infrastructure/Http/CustomerSignUpController'
import { COUNTRIES, flagEmoji, type PhoneCountry } from '@signUp/phoneCountries'

const { PnBaseData, PnSignUpData } = window
const prefill = PnSignUpData.prefill

const viewerIso2 = (PnSignUpData.viewerCountry || 'nz').toLowerCase()
const selectedCountry = ref<PhoneCountry>(
    COUNTRIES.find((c) => c.iso2 === viewerIso2) ?? COUNTRIES[0]!,
)
const phoneCode = computed(() => selectedCountry.value.dialCode)
const country = computed(() => selectedCountry.value.iso2)

const propertyName = ref('')
const firstName = ref(prefill.first_name)
const lastName = ref(prefill.last_name)
const email = ref(prefill.email)
const password = ref('')
const phoneNumber = ref('')
const numberOfUnits = ref(30)
const termsAccepted = ref(false)
const isValidating = ref(false)
const isSubmitting = ref(false)

const fieldErrors = reactive<Record<string, string[]>>({})

onMounted(() => {
    initHiddenFields()
})

function initHiddenFields() {
    const timezoneEl = document.getElementById('timezone') as HTMLInputElement
    const pageTitleEl = document.getElementById('page_title') as HTMLInputElement
    const productEl = document.getElementById('product') as HTMLInputElement
    const currencyEl = document.getElementById('currency') as HTMLInputElement

    timezoneEl.value = Intl.DateTimeFormat().resolvedOptions().timeZone
    pageTitleEl.value = document.title
    if (PnSignUpData.product) productEl.value = PnSignUpData.product
    if (PnSignUpData.currency) currencyEl.value = PnSignUpData.currency
}

function clearError(field: string) {
    delete fieldErrors[field]
}

function pushSignUpEvent() {
    window.dataLayer?.push({ event: 'trial_sign_up' })
}

function buildPayload() {
    return {
        property_name: propertyName.value,
        first_name: firstName.value,
        last_name: lastName.value,
        email: email.value,
        password: password.value,
        phone_number: phoneNumber.value,
        number_of_suites: numberOfUnits.value,
        terms: termsAccepted.value,
    }
}

async function validateField(field: string) {
    isValidating.value = true
    try {
        await axios.post(postSignUp.url(), buildPayload(), {
            headers: addPrecognitionHeaders({}, [field]),
        })
        delete fieldErrors[field]
    } catch (err) {
        if (err instanceof AxiosError && err.status === 422 && err.response?.data?.errors) {
            const { [field]: fieldError } = err.response.data.errors
            if (fieldError) {
                fieldErrors[field] = fieldError
            } else {
                delete fieldErrors[field]
            }
        }
    } finally {
        isValidating.value = false
    }
}

async function handleSubmit(event: Event) {
    event.preventDefault()

    isValidating.value = true
    try {
        await axios.post(postSignUp.url(), buildPayload(), {
            headers: addPrecognitionHeaders({}, []),
        })
        Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key])
    } catch (err) {
        if (err instanceof AxiosError && err.status === 422 && err.response?.data?.errors) {
            Object.assign(fieldErrors, err.response.data.errors)
        }
        isValidating.value = false
        return
    }
    isValidating.value = false
    ;(document.getElementById('phone') as HTMLInputElement).value =
        `+${phoneCode.value}${phoneNumber.value}`

    pushSignUpEvent()
    isSubmitting.value = true
    ;(event.target as HTMLFormElement).submit()
}
</script>

<template>
    <form
        method="post"
        name="signUpForm"
        id="signup-form"
        class="px-[10%] w-full mt-5"
        novalidate
        @submit="handleSubmit"
    >
        <input
            type="hidden"
            name="_token"
            :value="PnBaseData.security.csrfToken"
        />

        <div class="mb-4 md:mb-8">
            <img
                :src="'/img/sign-up/logo.svg'"
                alt="Preno"
                class="w-1/3 md:1/2"
            />
        </div>

        <div
            v-if="PnSignUpData.hasSubmitted"
            class="rounded border border-green-200 bg-green-50 text-green-800 p-3 mb-4"
        >
            <span class="font-bold text-xl">Success 🎉</span><br />
            One of our team members will be in contact shortly with the log-in details for your Free
            Trial.
        </div>

        <div
            v-if="PnSignUpData.sessionSubdomain"
            class="rounded border border-green-200 bg-green-50 text-green-800 p-3 mb-4"
        >
            <div class="font-bold text-xl">Welcome back!</div>
            <div>
                It appears that you already have an account with us.
                <a
                    :href="`https://${PnSignUpData.sessionSubdomain}.prenohq.com`"
                    class="font-bold"
                    >Click here to login.</a
                >
            </div>
        </div>

        <div>
            <div class="mb-1 md:mb-7.5 text-[1.2rem] md:text-[1.7rem] font-normal">
                <h1>Start your free trial</h1>
            </div>

            <div
                class="mb-4"
                id="property-name-form-group"
            >
                <FloatLabel
                    variant="on"
                    class="mt-2"
                >
                    <InputText
                        v-model="propertyName"
                        name="property_name"
                        id="property_name"
                        autocomplete="off"
                        :invalid="!!fieldErrors.property_name"
                        fluid
                        @blur="validateField('property_name')"
                        @input="clearError('property_name')"
                    />
                    <label for="property_name">Property name</label>
                </FloatLabel>
                <small
                    v-if="fieldErrors.property_name"
                    class="text-red-600"
                    >{{ fieldErrors.property_name[0] }}</small
                >
            </div>

            <div class="mb-6">
                <label>
                    Number of units (you can change this later)*:
                    <span>{{ numberOfUnits }}</span>
                </label>
                <Slider
                    v-model="numberOfUnits"
                    :min="1"
                    :max="201"
                    :step="1"
                    class="mt-2"
                />
                <input
                    type="hidden"
                    name="number_of_suites"
                    :value="numberOfUnits"
                />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 md:gap-5">
                <div class="mb-4">
                    <FloatLabel variant="on">
                        <InputText
                            v-model="firstName"
                            name="first_name"
                            id="first_name"
                            required
                            :invalid="!!fieldErrors.first_name"
                            fluid
                            @blur="validateField('first_name')"
                            @input="clearError('first_name')"
                        />
                        <label for="first_name">First name</label>
                    </FloatLabel>
                    <small
                        v-if="fieldErrors.first_name"
                        class="text-red-600"
                        >{{ fieldErrors.first_name[0] }}</small
                    >
                </div>
                <div class="mb-4">
                    <FloatLabel variant="on">
                        <InputText
                            v-model="lastName"
                            name="last_name"
                            id="last_name"
                            required
                            :invalid="!!fieldErrors.last_name"
                            fluid
                            @blur="validateField('last_name')"
                            @input="clearError('last_name')"
                        />
                        <label for="last_name">Last name</label>
                    </FloatLabel>
                    <small
                        v-if="fieldErrors.last_name"
                        class="text-red-600"
                        >{{ fieldErrors.last_name[0] }}</small
                    >
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 md:gap-5">
                <div class="mb-4">
                    <FloatLabel variant="on">
                        <InputText
                            v-model="email"
                            type="email"
                            name="email"
                            id="email"
                            required
                            :invalid="!!fieldErrors.email"
                            fluid
                            @blur="validateField('email')"
                            @input="clearError('email')"
                        />
                        <label for="email">Email</label>
                    </FloatLabel>
                    <small
                        v-if="fieldErrors.email"
                        class="text-red-600"
                        >{{ fieldErrors.email[0] }}</small
                    >
                </div>
                <div class="mb-4">
                    <FloatLabel variant="on">
                        <Password
                            v-model="password"
                            name="password"
                            input-id="password"
                            :feedback="false"
                            :invalid="!!fieldErrors.password"
                            toggle-mask
                            required
                            fluid
                            @blur="validateField('password')"
                            @input="clearError('password')"
                        />
                        <label for="password">Password</label>
                    </FloatLabel>
                    <small
                        v-if="fieldErrors.password"
                        class="text-red-600"
                        >{{ fieldErrors.password[0] }}</small
                    >
                </div>
            </div>

            <div class="mb-4">
                <div class="flex gap-2">
                    <Select
                        v-model="selectedCountry"
                        :options="COUNTRIES"
                        option-label="name"
                        filter
                        class="w-36 shrink-0"
                    >
                        <template #value="{ value }">
                            <span>{{ flagEmoji(value.iso2) }} +{{ value.dialCode }}</span>
                        </template>
                        <template #option="{ option }">
                            {{ flagEmoji(option.iso2) }} {{ option.name }} (+{{ option.dialCode }})
                        </template>
                    </Select>
                    <FloatLabel
                        variant="on"
                        class="flex-1 min-w-0"
                    >
                        <InputText
                            v-model="phoneNumber"
                            type="tel"
                            id="phone_number"
                            name="phone_number"
                            class="w-full"
                            :invalid="!!fieldErrors.phone_number"
                            @blur="validateField('phone_number')"
                            @input="clearError('phone_number')"
                        />
                        <label for="phone_number">Phone number</label>
                    </FloatLabel>
                </div>
                <small
                    v-if="fieldErrors.phone_number"
                    class="text-red-600"
                    >{{ fieldErrors.phone_number[0] }}</small
                >
            </div>

            <div class="mb-4">
                <div class="flex items-center gap-2">
                    <Checkbox
                        v-model="termsAccepted"
                        input-id="terms"
                        name="terms"
                        binary
                        required
                        :invalid="!!fieldErrors.terms"
                        @change="clearError('terms')"
                    />
                    <label for="terms">
                        I agree to
                        <a
                            class="text-blue-600 underline"
                            href="https://prenohq.com/terms"
                            target="_blank"
                            >terms of service</a
                        >
                        and
                        <a
                            class="text-blue-600 underline"
                            href="https://prenohq.com/privacy"
                            target="_blank"
                            >privacy policy</a
                        >
                    </label>
                </div>
                <small
                    v-if="fieldErrors.terms"
                    class="text-red-600"
                    >{{ fieldErrors.terms[0] }}</small
                >
            </div>

            <div class="mb-4">
                <Button
                    type="submit"
                    label="Start my free trial"
                    :loading="isSubmitting || isValidating"
                    fluid
                />
            </div>

            <div class="mb-4">
                <p class="text-center">No credit card required</p>
            </div>

            <input
                type="hidden"
                name="timezone"
                id="timezone"
            />
            <input
                type="hidden"
                id="page_url"
                name="page_url"
                :value="PnSignUpData.pageUrl"
            />
            <input
                type="hidden"
                id="page_title"
                name="page_title"
            />
            <input
                type="hidden"
                id="country"
                name="country"
                :value="country"
            />
            <input
                type="hidden"
                id="phone"
                name="phone"
            />
            <input
                type="hidden"
                id="phone_code"
                name="phone_code"
                :value="phoneCode"
            />
            <input
                type="hidden"
                id="product"
                name="product"
            />
            <input
                type="hidden"
                id="currency"
                name="currency"
            />
            <input
                type="hidden"
                id="lead_referer"
                name="lead_referer"
                :value="PnSignUpData.leadReferer"
            />
            <input
                type="hidden"
                id="xero_user_id"
                name="xero_user_id"
                :value="PnSignUpData.xeroUserId"
            />
        </div>
    </form>
</template>
